import { useId, type InputHTMLAttributes, type SelectHTMLAttributes, type ReactNode } from 'react';

interface FormFieldProps {
  label: string;
  /** id of the input the label targets. If omitted, a stable id is generated. */
  htmlFor?: string;
  required?: boolean;
  error?: string;
  hint?: string;
  children?: ReactNode;
}

export function FormField({ label, htmlFor, required, error, hint, children }: FormFieldProps) {
  const generatedId = useId();
  const inputId = htmlFor ?? generatedId;
  // We don't enforce that children is exactly the input — callers can render
  // any element. The label targets whatever id is provided (or the generated
  // fallback), so the click-target association always holds.
  return (
    <div className="form-group">
      <label htmlFor={inputId}>
        {label}
        {required && <span className="required">*</span>}
      </label>
      {hint && <p className="field-hint">{hint}</p>}
      {/* The child input must use the same id; helpers below pass it down. */}
      {children}
      {error && <p className="error-text">{error}</p>}
    </div>
  );
}

interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  required?: boolean;
  error?: string;
  hint?: string;
}

export function TextInput({ label, required, error, hint, id, ...props }: TextInputProps) {
  const fallbackId = useId();
  const inputId = id ?? fallbackId;
  return (
    <FormField label={label} required={required} error={error} hint={hint} htmlFor={inputId}>
      <input id={inputId} {...props} />
    </FormField>
  );
}

interface SelectInputProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  required?: boolean;
  error?: string;
  hint?: string;
  options: { value: string; label: string }[];
  placeholder?: string;
}

export function SelectInput({
  label,
  required,
  error,
  hint,
  id,
  options,
  placeholder,
  ...props
}: SelectInputProps) {
  const fallbackId = useId();
  const inputId = id ?? fallbackId;
  return (
    <FormField label={label} required={required} error={error} hint={hint} htmlFor={inputId}>
      <select id={inputId} {...props}>
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </FormField>
  );
}